import { api } from "./client";
import type { App } from "./apps.api";
import type { PanelUser } from "../store/auth.store";

export interface ImpersonateResponse {
  accessToken: string;
  expiresAt: string;
  user: PanelUser;
  impersonatedBy: { id: string; email: string; displayName: string | null };
}

export interface AdminUserRow {
  id: string;
  email: string;
  displayName: string | null;
  isPanelAdmin: boolean;
  disabled: boolean;
  lastLoginAt: string | null;
  lastLoginVia: string | null;
  createdAt: string;
  hasEntra: boolean;
  paycomId: string | null;
  mobilePhone: string | null;
  jobTitle: string | null;
  department: string | null;
  paycomSyncedAt: string | null;
}

export interface AdminUserDetail extends AdminUserRow {
  appAccess: Pick<App, "id" | "name" | "environment">[];
  backupCode: string | null;
}

export interface CreateUserInput {
  email: string;
  displayName?: string;
  isPanelAdmin?: boolean;
  appIds?: string[];
}

export interface UpdateUserInput {
  displayName?: string | null;
  isPanelAdmin?: boolean;
  disabled?: boolean;
}

export const usersApi = {
  create: (input: CreateUserInput) =>
    api.post<AdminUserRow>("/users", input).then((r) => r.data),

  list: (params?: { q?: string; panelAdminOnly?: boolean }) =>
    api
      .get<AdminUserRow[]>("/users", {
        params: {
          ...params,
          panelAdminOnly:
            params?.panelAdminOnly === undefined ? undefined : String(params.panelAdminOnly),
        },
      })
      .then((r) => r.data),

  get: (userId: string) =>
    api.get<AdminUserDetail>(`/users/${userId}`).then((r) => r.data),

  update: (userId: string, input: UpdateUserInput) =>
    api.patch<AdminUserRow>(`/users/${userId}`, input).then((r) => r.data),

  remove: (userId: string) =>
    api.delete(`/users/${userId}`).then(() => undefined),

  listApps: (userId: string) =>
    api.get<App[]>(`/users/${userId}/apps`).then((r) => r.data),

  setApps: (userId: string, appIds: string[]) =>
    api.put<App[]>(`/users/${userId}/apps`, { appIds }).then((r) => r.data),

  sendInvite: (userId: string) =>
    api.post(`/users/${userId}/invite`).then(() => undefined),

  setBackupCode: (userId: string, backupCode: string | null) =>
    api.put<AdminUserDetail>(`/users/${userId}/backup-code`, { backupCode }).then((r) => r.data),

  bulkUpdate: (userIds: string[], disabled: boolean) =>
    api
      .patch<{ updated: number; skipped: number }>("/users/bulk", { userIds, disabled })
      .then((r) => r.data),

  impersonate: (userId: string) =>
    api.post<ImpersonateResponse>(`/users/${userId}/impersonate`).then((r) => r.data),
};
